import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';

/**
 * Modal component for displaying planner PDF generation progress
 * Captures console.log('PDF_PROGRESS: ...') statements and displays them in real-time
 */
const PlannerGenerationModal = ({ isOpen, onClose, isGenerating }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const logContainerRef = useRef(null);
  const originalConsoleLog = useRef(null);

  // Intercept console.log to capture PDF_PROGRESS messages
  useEffect(() => {
    if (isOpen && isGenerating) {
      // Store original console.log
      originalConsoleLog.current = console.log;
      
      // Override console.log to capture PDF_PROGRESS messages
      console.log = (...args) => {
        // Call original console.log
        originalConsoleLog?.current(...args);
        
        // Check if message contains PDF_PROGRESS
        const message = args?.join(' ');
        if (message?.includes('PDF_PROGRESS:')) {
          const timestamp = new Date()?.toLocaleTimeString('en-US', { 
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
          
          // Extract clean message (remove PDF_PROGRESS: prefix)
          const cleanMessage = message?.replace('PDF_PROGRESS:', '')?.trim();
          
          // Add to logs
          setLogs(prev => [...prev, { timestamp, message: cleanMessage }]);
          
          // Calculate progress based on message content
          if (cleanMessage?.includes('Calculated') && cleanMessage?.includes('total pages')) {
            setProgress(5);
          } else if (cleanMessage?.includes('Cover page complete')) {
            setProgress(10);
          } else if (cleanMessage?.includes('Index page complete')) {
            setProgress(15);
          } else if (cleanMessage?.includes('Instruction page complete')) {
            setProgress(20);
          } else if (cleanMessage?.includes('Habit tracker pages complete')) {
            setProgress(25);
          } else if (cleanMessage?.includes('Year overview complete')) {
            setProgress(30);
          } else if (cleanMessage?.includes('monthly and weekly pages')) {
            setProgress(40);
          } else if (cleanMessage?.includes('organizational pages complete')) {
            setProgress(50);
          } else if (cleanMessage?.includes('Generating daily pages')) {
            setProgress(55);
          } else if (cleanMessage?.includes('Generated') && cleanMessage?.includes('of') && cleanMessage?.includes('days')) {
            // Extract progress from "Generated X of Y days" message
            const match = cleanMessage?.match(/Generated (\d+) of (\d+) days/);
            if (match) {
              const current = parseInt(match?.[1]);
              const total = parseInt(match?.[2]);
              // Daily pages take 55% to 95% of progress
              const dailyProgress = 55 + ((current / total) * 40);
              setProgress(Math.floor(dailyProgress));
            }
          } else if (cleanMessage?.includes('Daily pages complete')) {
            setProgress(95);
          } else if (cleanMessage?.includes('PDF generation complete')) {
            setProgress(100);
          }
        }
      };
      
      // Reset logs when modal opens
      setLogs([]);
      setProgress(0);
      
      // Add initial log
      setLogs([{ 
        timestamp: new Date()?.toLocaleTimeString('en-US', { 
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }), 
        message: 'Starting planner generation...' 
      }]);
    }
    
    // Cleanup: Restore original console.log when modal closes
    return () => {
      if (originalConsoleLog?.current) {
        console.log = originalConsoleLog?.current;
      }
    };
  }, [isOpen, isGenerating]);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logContainerRef?.current) {
      logContainerRef.current.scrollTop = logContainerRef?.current?.scrollHeight;
    }
  }, [logs]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    // Only allow closing if generation is complete
    if (progress === 100) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setLogs([]);
        setProgress(0);
        onClose?.();
      }, 200);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-opacity duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={(e) => {
        if (e?.target === e?.currentTarget && progress === 100) {
          handleClose();
        }
      }}
    >
      <div 
        className={`bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 overflow-hidden transition-transform duration-200 ${
          isClosing ? 'scale-95' : 'scale-100'
        }`}
      >
        {/* Header */}
        <div className="bg-primary px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {progress < 100 ? (
              <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Icon name="Check" size={24} className="text-white" />
              </div>
            )}
            <div>
              <h3 className="font-heading font-semibold text-white text-lg">
                {progress === 100 ? 'Generation Complete!' : 'Generating Planner'}
              </h3>
              <p className="font-body text-sm text-white/80">
                {progress > 0 ? `${progress}% complete` : 'Starting...'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={progress < 100}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={progress < 100 ? 'Please wait for generation to complete' : 'Close'}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-border">
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div 
              className="bg-primary h-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="font-caption text-xs text-muted-foreground mt-2 text-center">
            {progress === 100 
              ? 'Your planner is ready for download!' 
              : 'Please wait while your planner is being generated...'}
          </p>
        </div>

        {/* Live Log Feed */}
        <div className="px-6 py-4">
          <div 
            ref={logContainerRef}
            className="bg-muted rounded-lg p-4 h-64 overflow-y-auto font-mono text-xs space-y-1"
          >
            {logs?.length === 0 ? (
              <p className="text-muted-foreground">Initializing...</p>
            ) : (
              logs?.map((log, index) => (
                <div key={index} className="text-foreground">
                  <span className="text-muted-foreground">[{log?.timestamp}]</span>{' '}
                  <span>{log?.message}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-muted/50 border-t border-border">
          <p className="font-body text-sm text-muted-foreground text-center">
            {progress === 100 ? 
              'Close this window to proceed to download.' : 
              'Generation in progress. This may take several minutes depending on your planner configuration.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlannerGenerationModal;